import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "@lib/i18n";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import getDebug from "debug";
import { bool, func, number } from "prop-types";

import StickyRequestButton from "@components/shared/StickyRequestButton";
import JourneySwitch from "@components/PriceDetail/JourneySwitch";
import ConfirmationPopin from "./ConfirmationPopin";
import ErrorBoundary from "../shared/ErrorBoundary";
import CustomizeItem from "./CustomizeItem";
import TechnicalDetails from "../TechnicalDetails/TechnicalDetails";
import Financing from "../Financing/Financing";

import { useAvailableServiceCategories } from "@hooks/useAvailableServiceCategories";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { getIsLLDJourneySelected } from "@shared/v2/lib/getIsLLDJourneySelected";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import {
  useConfirmationPopinSelector,
  useIsAccessoriesDefaultOpenSelector,
} from "@redux/reducers/appConfig";
import { useCurrentFinancingData } from "@hooks/financing/useCurrentFinancingData";
import { useIsLLDPriceBreakdownRequestPendingSelector } from "@redux/reducers/requests";
import { useGetErrorMessage } from "@hooks/useGetErrorMessage";
import { useIsInitialRender } from "@hooks/useIsInitialRender";
import { useFinanceWidgetErrorsSelector } from "@redux/reducers/errors";
import { useIsUIv2Selector } from "@redux/reducers/appConfig";
import { resetFinanceWidgetErrorCodesAction } from "@redux/actions/errorsActions";
import { ROUTES } from "@shared/routing/routes";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { getIsProductionMode } from "@lib/getIsProductionMode";
import { useIsJourneySwitchEnabled } from "@hooks/useIsJourneySwitchEnabled";
import gtm from "@gtm/core";
import api from "@api";
import { useIsAllowedToContinueWithOrder } from "@hooks/useIsAllowedToContinueWithOrder";
import { useStepAwareCashData } from "@hooks/financing/useStepAwareCashData";

import {
  openCustomizeSectionEvent,
  homepageAddToCartEcommerceEvent,
} from "@gtm/events/homepage";

import {
  CTA_SOL_TYPES,
  DEAL_TYPE,
  CUSTOMIZE_TYPES,
  DEAL_STATES,
} from "@shared/constants";

import { getIsSCFJourneySelected } from "@shared/v2/lib/getIsSCFJourneySelected";

import { setIsGuestAction } from "@redux/actions/authActions";
import {
  postDealAction,
  createNewDealFromExistingAction,
  setIsSelectionConfirmedAction,
  setLastCtaAction,
  removePromoCodeAction,
  resetRegistrationPriceAction,
} from "@redux/actions/dealActions";
import { useCarSelector } from "@redux/reducers/appConfig";
import {
  useIsUserGuestSelector,
  useIsUserTrustedSelector,
} from "@redux/reducers/auth";
import {
  useActiveCarPreconfigurationSelector,
  useFinanceSimulationSelector,
  useCurrentDealStateSelector,
  useCarConfigurationSelector,
  useSalesmanSelector,
  useDealSelector,
} from "@redux/reducers/deal";

const debug = getDebug("app:components:Customize");

const { ACCESSORY, DESCRIPTION, PACK, SERVICE } = CUSTOMIZE_TYPES;

const getIncludedObjectsOfType = (options, currentOptions, desiredType) => {
  const { PACK, PERSONALIZATION } = CUSTOMIZE_TYPES;

  const getTypeCondition = type =>
    desiredType === PACK
      ? type === PACK || type === PERSONALIZATION
      : type === desiredType;

  if (!currentOptions) {
    return [];
  }

  return options.filter(
    ({ id, type }) => currentOptions.includes(id) && getTypeCondition(type)
  );
};

const Customize = ({
  isFinancingOpen,
  onIsFinancingOpenChange,
  fixedHeaderHeight,
  isPromoCodeApplicable,
}) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const financeSimulation = useFinanceSimulationSelector();
  const currentDealState = useCurrentDealStateSelector();
  const isLLDPriceBreakdownRequestPending = useIsLLDPriceBreakdownRequestPendingSelector();
  const selectedCarConfiguration = useCarConfigurationSelector();
  const availableServiceCategories = useAvailableServiceCategories();
  const salesman = useSalesmanSelector();
  const isUserTrusted = useIsUserTrustedSelector();
  const isUserGuest = useIsUserGuestSelector();
  const isB2BSelected = useIsB2BSelected();
  const isInitialRender = useIsInitialRender();
  const financeWidgetErrors = useFinanceWidgetErrorsSelector();
  const routingUtils = useRoutingUtils();
  const isAllowedToContinue = useIsAllowedToContinueWithOrder(
    isPromoCodeApplicable
  );
  const isProduction = getIsProductionMode();
  const deal = useDealSelector();
  const isPriceSwitcherEnabled = useIsJourneySwitchEnabled();
  const showConfirmationPopin = useConfirmationPopinSelector();
  const isAccessoriesDefaultOpen = useIsAccessoriesDefaultOpenSelector();

  const uiv2 = useIsUIv2Selector();
  const { options } = useCarSelector();

  const [isConfirmationPopinOpen, setIsConfirmationPopinOpen] = useState(false);

  const getErrorMessage = useGetErrorMessage();

  const {
    description,
    options: activeCarOptions,
  } = useActiveCarPreconfigurationSelector();
  const {
    financingLLDData,
    financingLOAData,
    financingVACData,
    financingSCFData,
  } = useCurrentFinancingData();

  const price = financeSimulation.totalPrice.cash.netPrice;
  const variant = selectedCarConfiguration.preconfiguration.label;

  const accessories = getIncludedObjectsOfType(
    options,
    activeCarOptions,
    ACCESSORY
  ).filter(
    accessory =>
      !accessory.salesmanBlacklist?.includes(salesman) &&
      accessory.status === true
  );

  const isCashJourneySelected = getIsCashJourneySelected(
    financeSimulation.journey
  );

  const isLLDJourneySelected = getIsLLDJourneySelected(
    financeSimulation.journey
  );

  const isSCFJourneySelected = getIsSCFJourneySelected(
    financeSimulation.journey
  );

  const gtmTitle = t(
    isCashJourneySelected ? "basket.myPayment.singular" : "basket.myFunding"
  );

  const packs = getIncludedObjectsOfType(
    options,
    activeCarOptions,
    PACK
  ).filter(option => option.status === true);

  const onConfirmButtonClick = () => {
    showConfirmationPopin ? setIsConfirmationPopinOpen(true) : navigateNext();
  };

  const hasDescriptionOrPack = description?.length > 0 || packs.length > 0;

  const onConfirmPopinButtonClick = async () => {
    setIsConfirmationPopinOpen(false);

    navigateNext();
  };

  const navigateNext = async () => {
    dispatch(resetRegistrationPriceAction());
    dispatch(resetFinanceWidgetErrorCodesAction());
    dispatch(removePromoCodeAction());

    if (isUserGuest) {
      await api.updateSession({ brandId: { isGuest: false } });
      dispatch(setIsGuestAction(false));
    }

    // If the deal is in state order or offer, clicking the proceed button should
    // lead to creation of a new deal object - which happens later on automatically
    // if currentDeal is null.. So by resetting current deal, we should achieve this..

    gtm.fire(
      homepageAddToCartEcommerceEvent({ price, variant, isB2BSelected })
    );

    const targetLastCta = CTA_SOL_TYPES.VEHICLE_SELECTED;

    if (
      isUserTrusted &&
      [DEAL_STATES.ORDER, DEAL_STATES.OFFER].includes(currentDealState)
    ) {
      await dispatch(createNewDealFromExistingAction());
      await dispatch(postDealAction(targetLastCta, DEAL_TYPE.DEAL, null));
    }

    dispatch(setLastCtaAction(targetLastCta));
    dispatch(setIsSelectionConfirmedAction(true));

    await api.updateSession({
      lastCta: targetLastCta,
      isSelectionConfirmed: true,
    });

    // @see https://github.com/zeit/next.js/issues/3249#issuecomment-342694715
    await router.push(
      ROUTES.BASKET,
      routingUtils.getLocalizedPathname(ROUTES.BASKET, null, i18n.language)
    );

    window.scrollTo(0, 0);

    return new Promise(() => null);
  };

  return (
    <div
      className="flex flex-col justify-start w-full gap-8"
      data-id="customize-container"
    >
      {uiv2 && isPriceSwitcherEnabled && (
        <ErrorBoundary>
          <div className="flex justify-center">
            <JourneySwitch isTextVersion isUIv2={uiv2} />
          </div>
        </ErrorBoundary>
      )}
      {hasDescriptionOrPack && (
        <ErrorBoundary>
          <CustomizeItem
            packages={packs}
            singleItem={packs.length === 1}
            title={DESCRIPTION}
            type={PACK}
            readOnly
          />
        </ErrorBoundary>
      )}
      <AnimatePresence>
        {isCashJourneySelected && availableServiceCategories?.length && (
          <motion.div
            animate="open"
            exit="collapsed"
            initial={isInitialRender ? "open" : "collapsed"}
            variants={{
              open: {
                display: "block",
                height: "auto",
                opacity: 1,
              },
              collapsed: {
                height: 0,
                opacity: 0,
                transition: { duration: 0.3, type: "tween" },
                transitionEnd: { display: "none" },
              },
            }}
          >
            <ErrorBoundary>
              <CustomizeItem
                packages={availableServiceCategories.map(service => ({
                  ...service,
                  type: CUSTOMIZE_TYPES.SERVICE,
                }))}
                singleItem={availableServiceCategories.length === 1}
                type={SERVICE}
              />
            </ErrorBoundary>
          </motion.div>
        )}
      </AnimatePresence>
      <ErrorBoundary>
        {accessories.length > 0 && (
          <CustomizeItem
            defaultOpen={isAccessoriesDefaultOpen}
            packages={accessories}
            singleItem={accessories.length === 1}
            type={ACCESSORY}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        <Financing
          deal={deal}
          cashData={useStepAwareCashData(true)}
          lldData={financingLLDData}
          loaData={financingLOAData}
          scfData={financingSCFData}
          vacData={financingVACData}
          errorMessages={financeWidgetErrors.map(getErrorMessage)}
          index={null}
          isGenericHeader
          isB2BSelected={isB2BSelected}
          isContainerOpen={isFinancingOpen}
          isCurrentStep={true}
          isLoading={isLLDJourneySelected && isLLDPriceBreakdownRequestPending}
          isReadOnly={isSCFJourneySelected}
          journey={financeSimulation.journey}
          onChange={isOpen => {
            onIsFinancingOpenChange(!isOpen);

            // isOpen might be also undefined, so we need strict check
            if (isOpen === false) {
              gtm.fire(openCustomizeSectionEvent(gtmTitle));
            }
          }}
          opened={isFinancingOpen}
          withDeliveryInfo
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <TechnicalDetails />
      </ErrorBoundary>
      <StickyRequestButton
        data-id="customize-order-button"
        triggerRequestCallback={onConfirmButtonClick}
        disabled={!isAllowedToContinue}
        fixedHeaderHeight={fixedHeaderHeight}
        isStickyToTop={true}
        isStickyToBottom={true}
        onRequestError={error => {
          if (isProduction) {
            return;
          }

          debug(error);
        }}
      >
        {t("customize.order")}
      </StickyRequestButton>
      <ConfirmationPopin
        isOpen={isConfirmationPopinOpen}
        onConfirm={onConfirmPopinButtonClick}
        onClose={() => setIsConfirmationPopinOpen(false)}
      />
    </div>
  );
};

Customize.propTypes = {
  isFinancingOpen: bool.isRequired,
  onIsFinancingOpenChange: func.isRequired,
  fixedHeaderHeight: number,
  isPromoCodeApplicable: bool,
};

export default Customize;
